import React, { useState, useCallback, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  MdRefresh,
  MdPlayArrow,
  MdSearch,
  MdSchedule,
  MdComputer,
  MdCheckCircle,
  MdWarning,
  MdError,
} from "react-icons/md";
import RoutinesService, {
  DebugAutoFinishResponse,
  PreEventD1DebugResponse,
} from "../../services/routines.service";
import { AuthContext } from "../../contexts/auth";
import { useHistory } from "react-router-dom";
import {
  Container,
  Header,
  TabsContainer,
  Tab,
  ContentCard,
  InfoGrid,
  InfoCard,
  InfoLabel,
  InfoValue,
  SectionTitle,
  ProjectsTable,
  StatusBadge,
  ButtonsContainer,
  ActionButton,
  LogsContainer,
  LogEntry,
  LogTimestamp,
  SearchInput,
  EmptyState,
} from "./styles";

interface LogItem {
  timestamp: string;
  message: string;
  type: "info" | "success" | "error" | "warning";
}

const D1_REASON_LABEL: Record<string, string> = {
  "missing-coordinator": "Sem 1º coordenador",
  "status-excluded": "Status encerrado",
  "already-sent": "E-mail desta data já enviado",
  "not-d-minus-one": "Não é o D-1 de hoje",
  "missing-template": "Template de e-mail não configurado",
  "missing-coordinator-email": "1º coordenador sem e-mail",
  "send-failed": "Falha no envio do e-mail",
};

function d1ActionLabel(
  project: PreEventD1DebugResponse["projects"][number],
  executed: boolean
) {
  if (!executed) {
    return project.retryEmailOnly ? "Reenviar e-mail" : "Disparar";
  }
  if (project.emailSent === false) {
    return "E-mail pendente";
  }
  if (project.emailSent === true) {
    return project.retryEmailOnly ? "E-mail reenviado" : "E-mail enviado";
  }
  return project.retryEmailOnly ? "E-mail reenviado" : "Disparado";
}

export function Routines() {
  const history = useHistory();
  const { user, userRoleName } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DebugAutoFinishResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [d1Data, setD1Data] = useState<PreEventD1DebugResponse | null>(null);
  const [d1Loading, setD1Loading] = useState(false);
  const [d1Error, setD1Error] = useState<string | null>(null);
  const [d1Search, setD1Search] = useState("");
  const [confirmD1, setConfirmD1] = useState(false);
  const [executingD1, setExecutingD1] = useState(false);

  // DEBUG
  console.log("[Routines Component]", {
    hasUser: !!user,
    userName: user?.name,
    userRoleId: user?.role_id,
    userRoleName,
  });

  // Verificar se o usuário é administrador (por role_id ou pelo nome do perfil)
  const isAdmin =
    user?.role_id === "USER_ADMINISTRATOR" || userRoleName === "Administrador";

  console.log("[Routines Component] isAdmin:", isAdmin);

  useEffect(() => {
    // Só redireciona se o user já carregou e não é admin
    if (user && !isAdmin) {
      console.log(
        "[Routines Component] Redirecting to /projetos because not admin"
      );
      history.push("/projetos");
    }
  }, [user, isAdmin, history]);

  const addLog = useCallback(
    (message: string, type: LogItem["type"] = "info") => {
      const now = new Date();
      const timestamp = now.toLocaleTimeString("pt-BR");
      setLogs((prev) => [...prev, { timestamp, message, type }]);
    },
    []
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    addLog("Buscando informações da rotina de finalização...", "info");

    try {
      const response = await RoutinesService.getDebugAutoFinish(false);
      setData(response);
      addLog(
        `Dados carregados com sucesso. ${response.debugInfo.totalCandidates} projetos candidatos encontrados.`,
        "success"
      );
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err.message || "Erro ao buscar dados";
      setError(errorMessage);
      addLog(`Erro ao buscar dados: ${errorMessage}`, "error");
    } finally {
      setLoading(false);
    }
  }, [addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchPreEventD1 = useCallback(async () => {
    setD1Loading(true);
    setD1Error(null);
    addLog("Buscando o lote D-1 de hoje...", "info");

    try {
      const response = await RoutinesService.getPreEventD1Debug(false);
      setD1Data(response);
      const ready = response.projects.filter(
        (project) => project.action === "execute"
      ).length;
      addLog(
        `Lote de hoje: ${response.examined} no D-1, ${ready} seriam disparados agora.`,
        "success"
      );
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err.message ||
        "Erro ao buscar o lote D-1";
      setD1Error(errorMessage);
      addLog(`Erro ao buscar o lote D-1: ${errorMessage}`, "error");
    } finally {
      setD1Loading(false);
    }
  }, [addLog]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchPreEventD1();
    }
  }, [activeTab, fetchPreEventD1]);

  const handleForcePreEventD1 = async () => {
    setConfirmD1(false);
    setExecutingD1(true);
    addLog("Disparando o lote D-1 de hoje...", "warning");

    try {
      const response = await RoutinesService.getPreEventD1Debug(true);
      setD1Data(response);
      addLog(
        `Lote de hoje disparado. Executados: ${response.executed}. E-mails: ${response.emailed}. Pendentes: ${response.emailPending}. Ignorados: ${response.skipped}.`,
        "success"
      );
      setSnackbar({
        open: true,
        message: `Alertas de hoje disparados. ${response.executed} projetos, ${response.emailed} e-mails.`,
        severity: "success",
      });
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err.message ||
        "Erro ao disparar o lote D-1";
      addLog(`Erro no disparo D-1: ${errorMessage}`, "error");
      setSnackbar({
        open: true,
        message: `Erro ao disparar os alertas de hoje: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setExecutingD1(false);
    }
  };

  const handleForceExecute = async () => {
    setConfirmDialog(false);
    setExecuting(true);
    addLog("Iniciando execução forçada da rotina de finalização...", "warning");

    try {
      const response = await RoutinesService.forceExecuteFinishRoutine();
      setData(response);

      if (response.executionResult.executed) {
        const finishedCount = response.executionResult.finishedProjects || 0;
        addLog(
          `Rotina executada com sucesso! ${finishedCount} projetos finalizados.`,
          "success"
        );
        setSnackbar({
          open: true,
          message: `Rotina executada com sucesso! ${finishedCount} projetos finalizados.`,
          severity: "success",
        });
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err.message ||
        "Erro ao executar rotina";
      addLog(`Erro na execução: ${errorMessage}`, "error");
      setSnackbar({
        open: true,
        message: `Erro ao executar rotina: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setExecuting(false);
    }
  };

  const filteredProjects =
    data?.projects?.filter((project) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        project.mmCode?.toString().includes(search) ||
        project.eventName?.toLowerCase().includes(search) ||
        project.projectId?.toLowerCase().includes(search)
      );
    }) || [];

  const renderTimeInfo = () => (
    <InfoGrid>
      <InfoCard>
        <InfoLabel>
          <MdSchedule style={{ marginRight: 8 }} />
          Horário API Externa
        </InfoLabel>
        <InfoValue color="#1976d2">
          {data?.debugInfo?.currentTimeFromApi || "Carregando..."}
        </InfoValue>
      </InfoCard>
      <InfoCard>
        <InfoLabel>
          <MdComputer style={{ marginRight: 8 }} />
          Timestamp Sistema
        </InfoLabel>
        <InfoValue>
          {data?.debugInfo?.timestamp
            ? new Date(data.debugInfo.timestamp).toLocaleString("pt-BR")
            : "Carregando..."}
        </InfoValue>
      </InfoCard>
      <InfoCard>
        <InfoLabel>Fuso Horário</InfoLabel>
        <InfoValue>
          {data?.debugInfo?.timezone || "America/Sao_Paulo"}
        </InfoValue>
      </InfoCard>
      <InfoCard>
        <InfoLabel>Offset GMT</InfoLabel>
        <InfoValue>{data?.debugInfo?.gmtOffset ?? -3}</InfoValue>
      </InfoCard>
    </InfoGrid>
  );

  const renderStats = () => (
    <InfoGrid>
      <InfoCard>
        <InfoLabel>Total de Candidatos</InfoLabel>
        <InfoValue color="#1976d2">
          {data?.debugInfo?.totalCandidates ?? 0}
        </InfoValue>
      </InfoCard>
      <InfoCard>
        <InfoLabel>
          <MdCheckCircle style={{ marginRight: 8, color: "#4caf50" }} />
          Serão Finalizados Amanhã
        </InfoLabel>
        <InfoValue color="#4caf50">
          {data?.debugInfo?.wouldFinish ?? 0}
        </InfoValue>
      </InfoCard>
      <InfoCard>
        <InfoLabel>
          <MdWarning style={{ marginRight: 8, color: "#ff9800" }} />
          Aguardando D+1
        </InfoLabel>
        <InfoValue color="#ff9800">
          {data?.debugInfo?.wouldNotFinish ?? 0}
        </InfoValue>
      </InfoCard>
      <InfoCard>
        <InfoLabel>Próxima Rotina</InfoLabel>
        <InfoValue>
          {data?.debugInfo?.nextRoutineDate ?? "N/A"} às 06:00
        </InfoValue>
      </InfoCard>
    </InfoGrid>
  );

  const renderProjectsTable = () => (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <SectionTitle style={{ margin: 0, border: "none", paddingBottom: 0 }}>
          Projetos Candidatos ({filteredProjects.length})
        </SectionTitle>
        <SearchInput
          placeholder="Buscar por MM Code ou nome..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      {filteredProjects.length === 0 ? (
        <EmptyState>
          <MdSearch size={48} />
          <Typography variant="body1" mt={2}>
            Nenhum projeto encontrado
          </Typography>
        </EmptyState>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <ProjectsTable>
            <thead>
              <tr>
                <th>MM Code</th>
                <th>Evento</th>
                <th>Status</th>
                <th>Fim do Evento</th>
                <th>D+1</th>
                <th>Próxima Rotina</th>
                <th>Será Finalizado?</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => (
                <tr key={project.projectId}>
                  <td>
                    <strong>MM{project.mmCode}</strong>
                  </td>
                  <td
                    style={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {project.eventName}
                  </td>
                  <td>
                    <StatusBadge variant="info">
                      {project.currentStatus}
                    </StatusBadge>
                  </td>
                  <td>{project.endDateFormatted}</td>
                  <td>{project.targetD1}</td>
                  <td>
                    {project.nextRoutineDate ||
                      data?.debugInfo?.nextRoutineDate}
                  </td>
                  <td>
                    {project.willFinishTomorrow ? (
                      <StatusBadge variant="success">✅ Sim</StatusBadge>
                    ) : (
                      <StatusBadge variant="warning">⏳ Não</StatusBadge>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: "#666" }}>
                    {project.reason}
                  </td>
                </tr>
              ))}
            </tbody>
          </ProjectsTable>
        </Box>
      )}
    </>
  );

  const renderLogs = () => (
    <>
      <SectionTitle>Logs de Execução</SectionTitle>
      <LogsContainer>
        {logs.length === 0 ? (
          <LogEntry type="info">Aguardando ações...</LogEntry>
        ) : (
          logs.map((log, index) => (
            <LogEntry key={index} type={log.type}>
              <LogTimestamp>[{log.timestamp}]</LogTimestamp>
              {log.message}
            </LogEntry>
          ))
        )}
      </LogsContainer>
    </>
  );

  const d1Projects =
    d1Data?.projects.filter((project) => {
      if (!d1Search) return true;
      const search = d1Search.toLowerCase();
      return (
        project.mmCode?.toString().includes(search) ||
        project.eventName?.toLowerCase().includes(search) ||
        project.projectId?.toLowerCase().includes(search)
      );
    }) || [];

  const d1ReadyCount =
    d1Data?.projects.filter((project) => project.action === "execute").length ??
    0;

  const renderPreEventTab = () => (
    <>
      <ButtonsContainer>
        <ActionButton
          onClick={fetchPreEventD1}
          disabled={d1Loading || executingD1}
          variant="secondary"
        >
          <MdRefresh size={18} />
          {d1Loading ? "Atualizando..." : "Atualizar prévia"}
        </ActionButton>
        <ActionButton
          onClick={() => setConfirmD1(true)}
          disabled={d1Loading || executingD1 || d1ReadyCount === 0}
          variant="primary"
        >
          <MdPlayArrow size={18} />
          {executingD1 ? "Disparando..." : "Disparar os de hoje"}
        </ActionButton>
      </ButtonsContainer>

      {d1Error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {d1Error}
        </Alert>
      )}

      <SectionTitle>Lote de hoje</SectionTitle>
      <InfoGrid>
        <InfoCard>
          <InfoLabel>No D-1 de hoje</InfoLabel>
          <InfoValue color="#1976d2">{d1Data?.examined ?? 0}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Seriam disparados agora</InfoLabel>
          <InfoValue color="#4caf50">{d1ReadyCount}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Ignorados</InfoLabel>
          <InfoValue color="#ff9800">{d1Data?.skipped ?? 0}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>E-mails no último disparo</InfoLabel>
          <InfoValue>{d1Data?.execute ? d1Data.emailed : "—"}</InfoValue>
        </InfoCard>
      </InfoGrid>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={3}
      >
        <SectionTitle style={{ margin: 0, border: "none", paddingBottom: 0 }}>
          Projetos ({d1Projects.length})
        </SectionTitle>
        <SearchInput
          placeholder="Buscar por MM Code ou nome..."
          value={d1Search}
          onChange={(e) => setD1Search(e.target.value)}
        />
      </Box>

      {d1Projects.length === 0 ? (
        <EmptyState>
          <MdSearch size={48} />
          <Typography variant="body1" mt={2}>
            Nenhum projeto no D-1 de hoje
          </Typography>
        </EmptyState>
      ) : (
        <Box style={{ overflowX: "auto" }}>
          <ProjectsTable>
            <thead>
              <tr>
                <th>MM Code</th>
                <th>Evento</th>
                <th>Ação</th>
                <th>Orçamento aprovado</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {d1Projects.map((project) => (
                <tr key={project.projectId}>
                  <td>
                    <strong>
                      {project.mmCode != null ? `MM${project.mmCode}` : "—"}
                    </strong>
                  </td>
                  <td
                    style={{
                      maxWidth: 240,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {project.eventName}
                  </td>
                  <td>
                    {project.action === "execute" ? (
                      <StatusBadge
                        variant={
                          d1Data?.execute && project.emailSent === false
                            ? "warning"
                            : "success"
                        }
                      >
                        {d1ActionLabel(project, Boolean(d1Data?.execute))}
                      </StatusBadge>
                    ) : (
                      <StatusBadge variant="warning">Ignorar</StatusBadge>
                    )}
                  </td>
                  <td>{project.budgetApproved ? "Sim" : "Não"}</td>
                  <td style={{ fontSize: 12, color: "#666" }}>
                    {project.reason
                      ? D1_REASON_LABEL[project.reason] || project.reason
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </ProjectsTable>
        </Box>
      )}

      {renderLogs()}
    </>
  );

  const renderFinishProjectsTab = () => (
    <>
      <ButtonsContainer>
        <ActionButton
          onClick={fetchData}
          disabled={loading}
          variant="secondary"
        >
          <MdRefresh size={18} />
          {loading ? "Atualizando..." : "Atualizar Dados"}
        </ActionButton>
        <ActionButton
          onClick={() => setConfirmDialog(true)}
          disabled={loading || executing}
          variant="danger"
        >
          <MdPlayArrow size={18} />
          {executing ? "Executando..." : "Forçar Finalização"}
        </ActionButton>
      </ButtonsContainer>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <SectionTitle>Informações de Tempo</SectionTitle>
      {renderTimeInfo()}

      <SectionTitle>Estatísticas</SectionTitle>
      {renderStats()}

      {renderProjectsTable()}

      {renderLogs()}
    </>
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <Container>
      <Header>
        <Typography variant="h5">Rotinas do Sistema</Typography>
      </Header>

      <TabsContainer>
        <Tab active={activeTab === 0} onClick={() => setActiveTab(0)}>
          Finalização de Projetos (D+1)
        </Tab>
        <Tab active={activeTab === 1} onClick={() => setActiveTab(1)}>
          Alerta pré-evento (D-1)
        </Tab>
      </TabsContainer>

      <ContentCard>
        {activeTab === 0 &&
          (loading && !data ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={300}
            >
              <CircularProgress />
            </Box>
          ) : (
            renderFinishProjectsTab()
          ))}
        {activeTab === 1 &&
          (d1Loading && !d1Data ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={300}
            >
              <CircularProgress />
            </Box>
          ) : (
            renderPreEventTab()
          ))}
      </ContentCard>

      {/* Dialog de Confirmação */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirmar Execução Forçada</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você está prestes a executar a rotina de finalização de projetos
            manualmente.
            <br />
            <br />
            <strong>Atenção:</strong> Esta ação irá finalizar automaticamente
            todos os projetos que atendem aos critérios de D+1 (projetos com
            status APPROVED cujo evento terminou há mais de 1 dia).
            <br />
            <br />
            Esta ação <strong>não pode ser desfeita</strong>.
            <br />
            <br />
            Projetos que serão finalizados:{" "}
            <strong>{data?.debugInfo?.wouldFinishToday ?? 0}</strong>
            {(data?.debugInfo?.wouldFinishTomorrow ?? 0) > 0 && (
              <>
                <br />
                <span style={{ fontSize: "0.9em", color: "#666" }}>
                  (+ {data?.debugInfo?.wouldFinishTomorrow ?? 0} projetos serão
                  finalizados na próxima rotina)
                </span>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleForceExecute}
            color="error"
            variant="contained"
          >
            Confirmar Execução
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmD1} onClose={() => setConfirmD1(false)}>
        <DialogTitle>Disparar os alertas D-1 de hoje</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Isso antecipa somente o lote de hoje: projetos cujo evento começa
            amanhã.
            <br />
            <br />
            O alerta aparece para o 1º coordenador, o líder e o administrador. O
            e-mail sai apenas para o 1º coordenador.
            <br />
            <br />
            Quem já recebeu o e-mail desta data de início é ignorado.
            <br />
            <br />
            Projetos que serão disparados agora: <strong>{d1ReadyCount}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmD1(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleForcePreEventD1}
            color="primary"
            variant="contained"
          >
            Disparar os de hoje
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
